<template>
  <div class="project-short-teaser" v-if="project">
    <b-row>
      <b-col lg="8" md="8" sm="12">
        <span v-if="!isSalesLead">
          <legend>
            <strong>{{ $t('shortTeaser') }}: {{ $t('generate_report') }}</strong>
          </legend>
          <b-row>
            <b-col lg="4" md="7" sm="7">
              <AttachmentList
                :attachments="shortTeaserAttachments"
                :attachmentType="shortTeaserAttachmentType"
                v-on:attachment-list:delete="onDeleteAttachment"
              />
            </b-col>
            <b-col lg="8" md="5" sm="5">
              <router-link
                class="btn btn-success pull-right"
                :to="{
                  name: 'ShortTeaserPrintPage',
                  params: { projectNumber: project.number },
                }"
                target="_blank"
              >
                <i class="fa fa-file mr-2"></i>{{ $t('preview') }}
              </router-link>
              <ButtonWait
                v-if="!isGuest"
                :startCallback="startShortTeaserBuild"
                :pollCallback="pollShortTeaserBuildState"
                class="pull-right mr-2"
              >
                <template v-slot:buttonText>
                  {{ $t('generateShortTeaser') }}
                </template>
              </ButtonWait>
            </b-col>
          </b-row>
        </span>
        <template v-if="isMasterPresentationVisible">
          <legend class="mt-3">
            <strong>{{ $t('masterPresentation') }}: {{ $t('generate_report') }}</strong>
          </legend>
          <b-row>
            <b-col lg="4" md="7" sm="7">
              <AttachmentList
                :attachments="masterPresentationAttachments"
                :attachmentType="masterPresentationAttachmentType"
                v-on:attachment-list:delete="onDeleteMasterPresentationAttachment"
              />
            </b-col>
            <b-col lg="8" md="5" sm="5">
              <router-link
                class="btn btn-success pull-right"
                :to="{
                  name: 'MasterPresentationPrintPage',
                  params: { projectNumber: project.number },
                }"
                target="_blank"
              >
                <i class="fa fa-file mr-2"></i>{{ $t('preview') }}
              </router-link>
              <ButtonWait
                v-if="!isGuest && !isSalesLead"
                :startCallback="startMasterPresentationBuild"
                :pollCallback="pollMasterPresentationBuildState"
                class="pull-right mr-2"
              >
                <template v-slot:buttonText>
                  {{ $t('generateMasterPresentation') }}
                </template>
              </ButtonWait>
            </b-col>
          </b-row>
        </template>

        <span v-if="!isSalesLead">
          <legend class="mt-3 mb-2">
            <strong>{{ $t('SHORT_TEASER_PROPERTY_IMAGE') }}</strong>
          </legend>
          <b-row>
            <b-col lg="12" md="12" sm="12">
              <DropzoneUpload
                v-if="!isGuest && project.id"
                class="mt-1"
                :complete="isShortTeaserPropertyImageUploadComplete"
                :parentId="project.id"
                :parentType="shortTeaserPropertyImageAttachmentType"
                :uploadCallback="onUploadShortTeaserPropertyImages"
                acceptedFileTypes=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                :multiUpload="true"
                v-on:dropzone-upload:reset="isShortTeaserPropertyImageUploadComplete = false"
              />
              <AttachmentList
                :attachments="shortTeaserPropertyImageAttachments"
                :attachmentType="shortTeaserPropertyImageAttachmentType"
                v-on:attachment-list:delete="onDeletePropertyImageAttachment"
              />
            </b-col>
          </b-row>
        </span>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ButtonWait from '@/components/ButtonWait.vue';
import AttachmentList from '@/components/AttachmentList';
import DropzoneUpload from '@/components/DropzoneUpload';

/** Matches {@link PROJECT_ATTACHMENT_TYPES.SHORT_TEASER} on the server */
const SHORT_TEASER_ATTACHMENT_TYPE = 'SHORT_TEASER';

/** Matches {@link PROJECT_ATTACHMENT_TYPES.SHORT_TEASER_PROPERTY_IMAGE} on the server */
const SHORT_TEASER_PROPERTY_IMAGE_ATTACHMENT_TYPE = 'SHORT_TEASER_PROPERTY_IMAGE';

/** Matches {@link PROJECT_ATTACHMENT_TYPES.MASTER_PRESENTATION} on the server */
const MASTER_PRESENTATION_ATTACHMENT_TYPE = 'MASTER_PRESENTATION';

/** Hardcoded: master presentation is only for this client */
const EWAY_ENERGY_CLIENT_NAME = 'E-way Energy GmbH';

export default {
  components: {
    ButtonWait,
    AttachmentList,
    DropzoneUpload,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      shortTeaserAttachmentType: SHORT_TEASER_ATTACHMENT_TYPE,
      masterPresentationAttachmentType: MASTER_PRESENTATION_ATTACHMENT_TYPE,
      shortTeaserPropertyImageAttachmentType: SHORT_TEASER_PROPERTY_IMAGE_ATTACHMENT_TYPE,
      isShortTeaserPropertyImageUploadComplete: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['isGuest', 'isSalesLead', 'isAdmin', 'getCurrentUser', 'isSales']),
    isMasterPresentationVisible() {
      const user = this.getCurrentUser;
      if (!user) {
        return false;
      }
      if (user.client && user.client.name === EWAY_ENERGY_CLIENT_NAME) {
        return true;
      }
      if (!user.clients) {
        return false;
      }
      return user.clients.some((client) => client.name === EWAY_ENERGY_CLIENT_NAME);
    },
    shortTeaserAttachments() {
      if (!this.project || !this.project.attachments) {
        return [];
      }
      return this.project.attachments.filter(
        (attachment) => attachment.attachmentType === SHORT_TEASER_ATTACHMENT_TYPE
      );
    },
    masterPresentationAttachments() {
      if (!this.project || !this.project.attachments) {
        return [];
      }
      return this.project.attachments.filter(
        (attachment) => attachment.attachmentType === MASTER_PRESENTATION_ATTACHMENT_TYPE
      );
    },
    shortTeaserPropertyImageAttachments() {
      if (!this.project || !this.project.attachments) {
        return [];
      }
      return this.project.attachments.filter(
        (attachment) => attachment.attachmentType === SHORT_TEASER_PROPERTY_IMAGE_ATTACHMENT_TYPE
      );
    },
  },
  methods: {
    ...mapActions([
      'buildShortTeaser',
      'buildMasterPresentation',
      'fetchAttachmentBuildState',
      'uploadProjectDynamicAttachment',
    ]),
    async startShortTeaserBuild() {
      if (this.project) {
        await this.buildShortTeaser(this.project.id);
      }
    },
    async pollShortTeaserBuildState() {
      return await this.fetchAttachmentBuildState({
        projectNumber: this.project.number,
        attachmentType: SHORT_TEASER_ATTACHMENT_TYPE,
      });
    },
    async startMasterPresentationBuild() {
      if (this.project) {
        await this.buildMasterPresentation(this.project.id);
      }
    },
    async pollMasterPresentationBuildState() {
      return await this.fetchAttachmentBuildState({
        projectNumber: this.project.number,
        attachmentType: MASTER_PRESENTATION_ATTACHMENT_TYPE,
      });
    },
    onDeleteAttachment(event) {
      this.$emit('project-short-teaser-attachment-list:delete', {
        attachmentId: event.attachmentId,
        attachmentType: SHORT_TEASER_ATTACHMENT_TYPE,
      });
    },
    onDeleteMasterPresentationAttachment(event) {
      this.$emit('project-short-teaser-attachment-list:delete', {
        attachmentId: event.attachmentId,
        attachmentType: MASTER_PRESENTATION_ATTACHMENT_TYPE,
      });
    },
    async onUploadShortTeaserPropertyImages(payload) {
      try {
        for (const file of payload.files) {
          await this.uploadProjectDynamicAttachment({
            file,
            parentId: payload.parentId,
            parentType: payload.parentType,
          });
        }
      } finally {
        this.isShortTeaserPropertyImageUploadComplete = true;
      }
    },
    onDeletePropertyImageAttachment(event) {
      this.$emit('project-short-teaser-attachment-list:delete', event);
    },
  },
};
</script>
